import { Button, Stack, Box, CircularProgress } from "@mui/material";
import MyLoadingButton from "./MyLoadingButton";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";
import {
  MUI_BUTTON_POSITIVE,
  MUI_BUTTON_NEGATIVE,
  MUI_BUTTON_BACK,
  MUI_BUTTON_NEXT,
} from "../../css/MuiClasses";

export const confirmButtonVariant = "contained";
export const cancelButtonVariant = "outlined";

export default function ConfirmAndCancelButtons({
  permissions = [],
  confirmTitle = "Save",
  cancelTitle = "Cancel",
  onCancel,
  onConfirm,
  isConfirmLoading = false,
  isCancelLoading = false,
  enableConfirmButton = true,
  useCustomLoadingButtonOnConfirm = false, // If true, renders MyLoadingButton instead of button
  useCustomLoadingButtonOnCancel = false,
  confirmButtonType = null,
  cancelButtonType = "button",
  confirmButtonProps = {},
  cancelButtonProps = {},
  confirmClassName = MUI_BUTTON_POSITIVE,
  cancelClassName = MUI_BUTTON_NEGATIVE,
  nextClassName = MUI_BUTTON_NEXT,
  backClassName = MUI_BUTTON_BACK,
  //stepper specifics
  backTitle = "Back",
  nextTitle = "Next",
  isStepper = false,
  onBack,
  onNext,
  backButtonType = null,
  nextButtonType = null,
  backButtonProps = {},
  nextButtonProps = {},
  testId,
  sx = {}, //container sx
}) {
  const { t } = useTranslation();

  if (Boolean(isStepper)) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ flexWrap: "wrap", rowGap: 3, columnGap: 2, width: "100%", ...sx }}
      >
        {/* Left side - Close */}
        <Button
          color="error"
          variant={cancelButtonVariant}
          className={cancelClassName}
          onClick={onCancel}
          type={cancelButtonType}
          data-testid={testId && `${testId}.cancel-button`}
          {...cancelButtonProps}
        >
          {t(cancelTitle)}
        </Button>

        {/* Middle - Previous & Next */}
        <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
          <Button
            variant="text"
            onClick={onBack}
            className={backClassName}
            type={backButtonType}
            data-testid={testId && `${testId}.back-button`}
            {...backButtonProps}
          >
            {t(backTitle)}
          </Button>
          <Button
            variant="text"
            onClick={onNext}
            className={nextClassName}
            type={nextButtonType}
            data-testid={testId && `${testId}.next-button`}
            {...nextButtonProps}
          >
            {t(nextTitle)}
          </Button>
        </Stack>

        {/* Right side - Confirm */}
        {enableConfirmButton && (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={onConfirm}
              className={confirmClassName}
              type={confirmButtonType}
              data-testid={testId && `${testId}.confirm-button`}
              {...confirmButtonProps}
            >
              {t(confirmTitle)}
            </Button>
          </Stack>
        )}
      </Stack>
    );
  } else {
    // Confirm and Cancel buttons
    return (
      <Stack
        justifyContent={"end"}
        direction={"row"}
        spacing={1}
        display="flex"
        alignItems="center"
        sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 2, width: "100%", ...sx }}
      >
        {Boolean(enableConfirmButton) &&
          (Boolean(useCustomLoadingButtonOnConfirm) ? (
            <MyLoadingButton
              message={confirmTitle}
              permissions={permissions}
              isLoading={isConfirmLoading}
              size="medium"
              onClick={onConfirm}
              className={confirmClassName}
              type={confirmButtonType}
              data-testid={testId && `${testId}.confirm-button`}
              {...confirmButtonProps}
            />
          ) : (
            <Button
              variant={confirmButtonVariant}
              onClick={onConfirm}
              size="medium"
              disabled={isConfirmLoading}
              className={confirmClassName}
              type={confirmButtonType}
              data-testid={testId && `${testId}.confirm-button`}
              {...confirmButtonProps}
            >
              {t(confirmTitle)}
            </Button>
          ))}
        {/* Render the cancel button if cancelTitle is not an empty string. This is added because of because of useConfirm's dialogue popups logic. */}
        {Boolean(cancelTitle) &&
          (Boolean(useCustomLoadingButtonOnCancel) ? (
            <MyLoadingButton
              onClick={onCancel}
              variant={cancelButtonVariant}
              color="error"
              loading={isCancelLoading}
              message={cancelTitle}
              className={cancelClassName}
              type={cancelButtonType}
              data-testid={testId && `${testId}.cancel-button`}
              {...cancelButtonProps}
            />
          ) : (
            <Button
              color="error"
              size="medium"
              onClick={onCancel}
              className={cancelClassName}
              disabled={isCancelLoading}
              variant={cancelButtonVariant}
              type={cancelButtonType}
              data-testid={testId && `${testId}.cancel-button`}
              {...cancelButtonProps}
            >
              {t(cancelTitle)}
            </Button>
          ))}
      </Stack>
    );
  }
}
